import React, { useEffect, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import '../styles/auth.css';
import { loginUser, saveUserSession } from '../services/authService';

const MailIcon = () => (
  <svg className="auth-field-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="5" width="18" height="14" rx="2" />
    <path d="M3 7l9 6 9-6" />
  </svg>
);

const LockIcon = () => (
  <svg className="auth-field-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="5" y="11" width="14" height="10" rx="2" />
    <path d="M8 11V8a4 4 0 118 0v3" />
  </svg>
);

const EyeIcon = () => (
  <svg className="auth-field-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const GoogleIcon = () => (
  <svg className="auth-social-icon" viewBox="0 0 24 24" aria-hidden="true">
    <path fill="#EA4335" d="M12 11.2v2.4h4.2c-.2 1-1.4 2.9-4.2 2.9-2.5 0-4.6-2.1-4.6-4.7s2.1-4.7 4.6-4.7c1.4 0 2.4.6 3 1.1l2-1.9C15.5 5.4 13.9 4.8 12 4.8 7.9 4.8 4.5 8.2 4.5 12.3S7.9 19.8 12 19.8c4.4 0 7.3-3.1 7.3-7.5 0-.5 0-.9-.1-1.3H12z" />
  </svg>
);

const FacebookIcon = () => (
  <svg className="auth-social-icon" viewBox="0 0 24 24" fill="#1877F2" aria-hidden="true">
    <path d="M13.5 8.5H15V6h-1.5C11.6 6 10.5 7.1 10.5 9v1.5H9v2.5h1.5V18h2.8v-5H15l.4-2.5h-2.9V9c0-.8.2-1.5 1-1.5z" />
  </svg>
);

const AppleIcon = () => (
  <svg className="auth-social-icon" viewBox="0 0 24 24" fill="#111827" aria-hidden="true">
    <path d="M16.7 13.1c-.1-2.2 1.8-3.3 1.9-3.4-1-.1-2.1.6-2.6.6-.6 0-1.4-.6-2.3-.6-1.2 0-2.3.7-2.9 1.8-1.2 2.1-.3 5.3.9 7 .6.9 1.3 1.9 2.2 1.9.9 0 1.2-.6 2.3-.6 1.1 0 1.4.6 2.3.5.9 0 1.5-.9 2.1-1.8.7-1 .9-2 1-2-.1 0-1.8-.7-1.8-2.7zM14.9 5.2c.5-.6.9-1.5.8-2.3-.8 0-1.7.5-2.2 1.1-.5.6-.9 1.5-.8 2.3.9.1 1.8-.4 2.2-1.1z" />
  </svg>
);

const RegisterPage = () => {
  const history = useHistory();
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [allowAutofill, setAllowAutofill] = useState(false);

  useEffect(() => {
    setEmail('');
    setPassword('');

    // El navegador a veces rellena después del primer render.
    const timer = window.setTimeout(() => {
      setEmail('');
      setPassword('');
    }, 50);

    return () => {
      window.clearTimeout(timer);
    };
  }, []);

  const handleSubmit = (event) => {
    event.preventDefault();
    setErrorMessage('');
    setIsSubmitting(true);

    loginUser({ email, password })
      .then((user) => {
        saveUserSession(user, rememberMe);
        history.push('/perfil');
      })
      .catch((error) => {
        setErrorMessage(error.message || 'No se pudo iniciar sesión.');
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  return (
    <div className="auth-root">
      <section className="auth-section">
        <div className="auth-shell">
          <div className="auth-visual">
            <img
              src={`${process.env.PUBLIC_URL}/images/register-login-photo.png`}
              alt="Niña en el aula"
              className="auth-photo"
            />
          </div>

          <div className="auth-panel">
            <div className="auth-panel-inner">
              <h1 className="auth-title">Hola, Inicia Sesión</h1>
              <p className="auth-subtitle">
                Ingresa los datos de tu cuenta para iniciar sesión. Si no tienes una cuenta{' '}
                <Link to="/registro/crear-cuenta" className="auth-inline-link">
                  Regístrate
                </Link>
              </p>

              <form className="auth-form" onSubmit={handleSubmit} autoComplete="off">
                <label className="auth-field">
                  <span className="auth-field-label">Correo electrónico</span>
                  <span className="auth-field-control">
                    <MailIcon />
                    <input
                      type="email"
                      name="login_email"
                      placeholder="Correo electrónico"
                      value={email}
                      onChange={(event) => setEmail(event.target.value)}
                      onFocus={() => setAllowAutofill(true)}
                      autoComplete="off"
                      readOnly={!allowAutofill}
                      required
                    />
                  </span>
                </label>

                <label className="auth-field">
                  <span className="auth-field-label">Contraseña</span>
                  <span className="auth-field-control">
                    <LockIcon />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      name="login_password"
                      placeholder="Contraseña"
                      value={password}
                      onChange={(event) => setPassword(event.target.value)}
                      onFocus={() => setAllowAutofill(true)}
                      autoComplete="new-password"
                      readOnly={!allowAutofill}
                      required
                    />
                    <button
                      type="button"
                      className="auth-password-toggle"
                      aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                      onClick={() => setShowPassword((value) => !value)}
                    >
                      <EyeIcon />
                    </button>
                  </span>
                </label>

                <div className="auth-form-options">
                  <label className="auth-remember">
                    <input
                      type="checkbox"
                      checked={rememberMe}
                      onChange={(event) => setRememberMe(event.target.checked)}
                    />
                    <span>Mantenerme conectado</span>
                  </label>
                  <Link to="/recuperar-contraseña" className="auth-forgot-link">
                    ¿Olvidaste tu contraseña?
                  </Link>
                </div>

                {errorMessage ? <p className="register-error">{errorMessage}</p> : null}

                <button type="submit" className="auth-submit" disabled={isSubmitting}>
                  {isSubmitting ? 'Iniciando...' : 'Iniciar sesión'}
                </button>
              </form>

              <div className="auth-divider">
                <span>O inicia con</span>
              </div>

              <div className="auth-social-buttons">
                <button type="button" className="auth-social-btn">
                  <GoogleIcon />
                  Google
                </button>
                <button type="button" className="auth-social-btn">
                  <FacebookIcon />
                  Facebook
                </button>
                <button type="button" className="auth-social-btn">
                  <AppleIcon />
                  Apple
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default RegisterPage;
